<template>
    <div ref="tournament_summary" class="tournament-summary">
        <h2 class="title">{{ $t('lastCheck') }}</h2>
        <p class="subtitle">{{ $t('confirmDetails') }}</p>

        <div class="info-card">
            <div class="summary-container">
                <div class="summary-box">
                    <span v-for="(item, index) in summaryItems" :key="index" class="summary-item">
                        <i :class="'icon ' + item.icon"></i>
                        <span v-html="item.value"></span>
                    </span>
                    <span class="confirm-section">
                        <input class="confirm-features" type="checkbox" v-model="confirmFeatures"
                            name="confirm-features">
                        <label class="confirm-features-lbl" for="confirm-features">{{ $t('confirmCheckbox') }}</label>
                    </span>
                </div>
                <lottie class="animation" style="margin: 0;" :options="lottieOptions" :height="300" :width="300" />
            </div>
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex';
import * as animationData from "@/assets/animations/check-plan.json";

export default {
    name: 'TournamentSummary',
    props: {
        stepActive: {
            type: Boolean,
            required: true
        },
        setNextBtnDisabled: {
            type: Function,
            required: true
        }
    },
    data: () => ({
        confirmFeatures: false, // Ensure it starts unchecked
        lottieOptions: { animationData: animationData.default }
    }),
    computed: {
        ...mapState({
            form: state => state.tournamentForm
        }),
        rankingTypeText() {
            const rankingTypes = {
                'SWISET_RATIO': 'Swiset Ratio',
                'BALANCE': 'Balance-Base',
                'EQUITY_RT': 'Raw Equity-Base',
                'PROFIT_PERCENTAGE': 'Percentage Equity-Base',
                'PROFIT': 'Profit',
                'EQUITY_PERCENTAGE': 'Equity Percentage',
                'PROFIT_FACTOR': 'Profit Factor',
                'AVG_WIN_LOSS_RATIO': 'Average Win/Loss Ratio',
                'EQUITY_ROI_PERCENTAGE': 'Equity ROI Percentage'
            };
            return rankingTypes[this.form.participants.rankingType] || this.form.participants.rankingType;
        },
        visibilityText() {
            const visibilityType = this.form.details.visibility.toLowerCase();
            // In Spanish, adjective goes after the noun
            if (this.$i18n.locale === 'es') {
                return `${this.$t('texts.tournament')} ${this.$t(`texts.${visibilityType}`)}`;
            }
            // In English, adjective goes before the noun
            return `${this.$t(`texts.${visibilityType}`)} ${this.$t('texts.tournament')}`;
        },
        summaryItems() {
            return [
                {
                    icon: 'fa-regular fa-trophy',
                    value: `<b>${this.$t('texts.tournamentName')}</b> ${this.form.basicInfo.name}`
                },
                {
                    icon: 'fa-regular fa-calendar',
                    value: `<b>${this.$t('texts.dates')}</b> ${this.getFormattedDateRange()}`
                },
                {
                    icon: 'fa-regular fa-chart-line',
                    value: `<b>${this.$t('texts.rankingType')}</b> ${this.rankingTypeText}`
                },
                {
                    icon: 'fa-regular fa-eye',
                    value: `<b>${this.$t('texts.visibility')}</b> ${this.visibilityText}`
                },
                {
                    icon: 'fa-regular fa-list',
                    value: `<b>${this.$t('texts.rules')}</b> ${this.getRulesCount()} ${this.$t('texts.rulesIncluded')}`
                },
                {
                    icon: 'fa-regular fa-award',
                    value: `<b>${this.$t('texts.awards')}</b> ${this.form.awards.length} ${this.$t('texts.awards')} (US$${this.getTotalAwardsValue()}K ${this.$t('texts.estimatedValue')})`
                }
            ];
        }
    },
    methods: {
        getFormattedDateRange() {
            // Parse dates as local dates to avoid timezone conversion issues
            const [startYear, startMonth, startDay] = this.form.basicInfo.startDate.split('-');
            const [endYear, endMonth, endDay] = this.form.basicInfo.endDate.split('-');

            const startDate = new Date(startYear, startMonth - 1, startDay);
            const endDate = new Date(endYear, endMonth - 1, endDay);
            const days = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));

            return `${this.$t('texts.from')} ${startDate.toLocaleDateString()} ${this.$t('texts.to')} ${endDate.toLocaleDateString()} (${days} ${this.$t('texts.days')})`;
        },
        getTotalAwardsValue() {
            const total = this.form.awards.reduce((sum, award) => sum + parseFloat(award.value || 0), 0);
            return (total / 1000).toFixed(1);
        },
        getRulesCount() {
            // Count regular rules
            let count = this.form.rules.length;

            // Add 1 for initial balance if tournament is self-managed and has initial balance
            if (this.form.details.selfManaged && this.form.initialBalance) {
                count += 1;
            }

            return count;
        }
    },
    mounted() {
        // Ensure button is disabled when component mounts
        this.setNextBtnDisabled(true);
    },
    watch: {
        confirmFeatures(newValue) {
            // Update next button state based on checkbox
            this.setNextBtnDisabled(!newValue);
        },
        stepActive: {
            immediate: true,
            handler(isActive) {
                if (this.$refs.tournament_summary) {
                    this.$refs.tournament_summary.style.width = isActive ? '100%' : '0';
                    this.$refs.tournament_summary.style.height = isActive ? '100%' : '0';
                    if (isActive) {
                        // When step becomes active, ensure next button is disabled until checkbox is checked
                        this.setNextBtnDisabled(true);
                    }
                }
            }
        }
    }
}
</script>



<style lang="scss" scoped>
.tournament-summary {
    display: flex;
    width: 100%;
    flex-direction: column;
    font-family: "Avenir", sans-serif;

    .title {
        font-size: 32px !important;
        font-weight: bold;
        margin-bottom: 8px;
        color: var(--v-textPrimary-base);
    }

    .subtitle {
        font-size: 16px;
        color: var(--v-textSecondary-base);
        margin-bottom: 24px;
    }

    .info-card {
        background-color: var(--v-cardBackground-base);
        border: 1px solid #9A9A9A;
        border-radius: 10px;
        padding: 76px 60px;

        .summary-container {
            display: flex;
            justify-content: center;
            align-items: center;

            .summary-box {
                display: flex;
                flex-direction: column;

                .summary-item {
                    font-size: 18px;
                    margin-bottom: 16px;
                    display: flex;
                    align-items: center;

                    .icon {
                        color: var(--v-mainGreen-base) !important;
                        width: 24px;
                        margin-right: 16px;
                        margin-bottom: 5px;
                    }
                }

                .confirm-section {
                    display: flex;
                    align-items: center;
                    margin-top: 40px;
                }
            }
        }
    }

    .confirm-features {
        -moz-appearance: none;
        -webkit-appearance: none;
        -o-appearance: none;
        appearance: none;
        border-radius: 5px;
        border: 1px solid #9A9A9A;
        accent-color: var(--v-mainGreen-base);
        margin-right: 20px;
        width: 18px;
        height: 18px;
        transition: box-shadow 0.15s ease-in-out;
        cursor: pointer;

        &:hover {
            box-shadow: 0px 4px 5px 0 rgba(0, 0, 0, 0.25);
        }

        &::before {
            display: block;
            position: relative;
            content: "";
            background-image: url('data:image/svg+xml,<svg width="12" height="10" viewBox="0 0 12 10" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11.5879 1.07812C11.9134 1.40365 11.9134 1.93229 11.5879 2.25781L4.92122 8.92448C4.5957 9.25 4.06706 9.25 3.74154 8.92448L0.408203 5.59115C0.0826823 5.26563 0.0826823 4.73698 0.408203 4.41146C0.733724 4.08594 1.26237 4.08594 1.58789 4.41146L4.33268 7.15365L10.4108 1.07812C10.7363 0.752604 11.265 0.752604 11.5905 1.07812H11.5879Z" fill="white"/></svg>');
            color: white;
            background-position: center;
            transform: scale(0);
            background-color: var(--v-mainGreen-base);
            left: -1px;
            top: -2px;
            border-radius: 5px;
            padding: 10px;
        }

        &:checked {
            &::before {
                transform: scale(1);

            }
        }
    }

    .confirm-features-lbl {
        font-size: 16px;
    }

    @media (max-width: 768px) {
        .info-card {
            padding: 40px 20px;

            .summary-container {
                flex-direction: column;

                .animation {
                    display: none;
                }
            }
        }
    }
}
</style>
